import Markdown from "react-markdown";
import { X, Download, FileText, FileType, FileSpreadsheet } from "lucide-react";

const MONO = "'JetBrains Mono', ui-monospace, monospace";
const money = (n) => "$" + (n >= 1e6 ? (n / 1e6).toFixed(2) + "M" : n >= 1e3 ? (n / 1e3).toFixed(0) + "K" : Number(n || 0).toFixed(2));

// Build a clean, board-ready markdown document from a swarm run result.
export function resultToMarkdown(r) {
  if (!r) return "";
  const company = r.mode === "jnj" ? "Johnson & Johnson" : "Tailored Brands";
  const s = r.summary || {};
  const c = r.comparison || {};
  const L = [];
  L.push(`# QNNEAL Quantum Optimization Report`);
  L.push(`**${company}** · Run \`${r.run_id}\``);
  L.push("");
  L.push(`> ${r.prompt}`);
  L.push("");
  L.push(`## Executive Summary`);
  L.push(r.executive_summary || "Quantum-optimized allocation analysis complete.");
  L.push("");
  L.push(`**Quantum uplift: ${money(s.quantum_uplift_usd)}**  ·  classical-equivalent compute: ${s.classical_equivalent || "n/a"}`);
  L.push("");
  const q = r.quantum_summary;
  if (q && q.variables) {
    L.push(`## Quantum Optimization Applied`);
    L.push(`| Metric | Value |`);
    L.push(`| --- | --- |`);
    L.push(`| QUBO variables | ${q.variables.toLocaleString()} |`);
    L.push(`| Solve time | ${q.solve_time_s}s |`);
    L.push(`| Optimality gap | ${q.optimality_gap} |`);
    L.push(`| Hardware | ${q.hardware} |`);
    L.push("");
    L.push(q.comparison_line || "");
    L.push("");
  }
  if ((r.scope_lines || []).length) {
    L.push(`## Analysis Scope`);
    r.scope_lines.forEach((x) => L.push(`- **${x.label}:** ${x.value}`));
    L.push("");
  }
  L.push(`## Key Metrics`);
  L.push(`| Metric | Value |`);
  L.push(`| --- | --- |`);
  L.push(`| Locations analyzed | ${(s.stores_analyzed || 0).toLocaleString()} |`);
  L.push(`| SKUs / compounds evaluated | ${(s.skus_evaluated || 0).toLocaleString()} |`);
  L.push(`| Combinations optimized | ${(s.combinations_optimized || 0).toLocaleString()} |`);
  L.push(`| Solve time | ${s.solve_time_seconds}s |`);
  L.push("");
  L.push(`## Top Findings`);
  (r.findings || []).forEach((f) => {
    L.push(`### ${f.rank}. ${f.title}: +${money(f.impact_usd)}`);
    L.push(f.detail || "");
    const pb = f.playbook || {};
    if (pb.what) L.push(`- **What:** ${pb.what}`);
    if (pb.why) L.push(`- **Why:** ${pb.why}`);
    if ((pb.how || []).length) {
      L.push(`- **How:**`);
      pb.how.forEach((st) => L.push(`  * ${st.phase}, ${st.owner}: ${st.action}`));
    }
    L.push("");
  });
  L.push(`## Quantum vs Classical`);
  L.push(`| Optimizer | Value |`);
  L.push(`| --- | --- |`);
  L.push(`| Classical | ${money(c.classical_value_usd)} |`);
  L.push(`| Quantum | ${money(c.quantum_value_usd)} |`);
  L.push(`| Uplift | +${money(c.uplift_usd)} (${c.uplift_pct}%) |`);
  L.push("");
  const cm = r.compute_method || {};
  L.push(`## Methodology`);
  L.push(`Compute method: **${cm.label || "n/a"}** · variables ${cm.variables || "n/a"} · solve ${cm.solve_time_ms || "n/a"}ms.`);
  L.push("");
  if ((r.followups || []).length) {
    L.push(`## Follow-up Q&A`);
    r.followups.forEach((f, i) => {
      L.push(`**Q${i + 1}. ${f.q}**`);
      L.push(f.a || "");
      L.push("");
    });
  }
  L.push(`_${r.data_disclaimer || ""}_`);
  return L.join("\n");
}

const mdComponents = {
  h1: (p) => <h1 className="text-2xl font-bold text-slate-100 mt-2 mb-3" style={{ fontFamily: "'Space Grotesk',sans-serif" }} {...p} />,
  h2: (p) => <h2 className="text-lg font-bold text-[#14F0C8] mt-6 mb-2 border-b border-white/[0.08] pb-1" {...p} />,
  h3: (p) => <h3 className="text-base font-semibold text-slate-100 mt-4 mb-1" {...p} />,
  p: (p) => <p className="text-[13px] text-slate-300 leading-relaxed my-2" {...p} />,
  ul: (p) => <ul className="list-disc list-inside text-[13px] text-slate-300 space-y-1 my-2" {...p} />,
  li: (p) => <li className="text-[13px] text-slate-300" {...p} />,
  strong: (p) => <strong className="text-slate-100 font-semibold" {...p} />,
  blockquote: (p) => <blockquote className="border-l-2 border-[#14F0C8]/50 pl-3 text-slate-400 italic my-3" {...p} />,
  table: (p) => <table className="w-full text-[12px] my-3 border border-white/[0.08] rounded overflow-hidden" style={{ fontFamily: MONO }} {...p} />,
  th: (p) => <th className="text-left px-3 py-1.5 bg-white/[0.04] text-slate-400 font-semibold border-b border-white/[0.08]" {...p} />,
  td: (p) => <td className="px-3 py-1.5 text-slate-300 border-b border-white/[0.05]" {...p} />,
  em: (p) => <em className="text-slate-500 text-[11px]" {...p} />,
};

export function MarkdownDoc({ md, title, onClose, onExport, exporting }) {
  const download = () => {
    const blob = new Blob([md], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(title || "QNNEAL_Report").replace(/[^a-z0-9]+/gi, "_")}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="fixed inset-0 z-[70] flex flex-col bg-[#05070E]" data-testid="markdown-doc">
      <div className="flex-shrink-0 flex items-center justify-between px-6 py-3 border-b border-white/[0.08] bg-[#070B14]/90 backdrop-blur-xl">
        <div className="flex items-center gap-2 text-[#14F0C8]">
          <FileText className="w-5 h-5" />
          <span className="text-sm font-bold tracking-wide">{title || "Optimization Report"}.md</span>
        </div>
        <div className="flex items-center gap-2">
          {onExport && (
            <>
              <button onClick={() => onExport("pdf")} disabled={exporting} data-testid="doc-pdf-btn"
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/[0.12] hover:border-[#14F0C8]/60 text-slate-200 transition-colors disabled:opacity-50">
                <FileText className="w-3.5 h-3.5" /> PDF
              </button>
              <button onClick={() => onExport("docx")} disabled={exporting} data-testid="doc-word-btn"
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/[0.12] hover:border-[#14F0C8]/60 text-slate-200 transition-colors disabled:opacity-50">
                <FileType className="w-3.5 h-3.5" /> Word
              </button>
              <button onClick={() => onExport("xlsx")} disabled={exporting} data-testid="doc-excel-btn"
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/[0.12] hover:border-[#14F0C8]/60 text-slate-200 transition-colors disabled:opacity-50">
                <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
              </button>
            </>
          )}
          <button onClick={download} data-testid="doc-download-btn"
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-[#14F0C8]/40 bg-[#14F0C8]/10 hover:border-[#14F0C8] text-[#14F0C8] transition-colors">
            <Download className="w-3.5 h-3.5" /> Download .md
          </button>
          <button onClick={onClose} data-testid="doc-close-btn" className="p-1.5 text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-6 py-8">
        <div className="max-w-3xl mx-auto">
          <Markdown components={mdComponents}>{md}</Markdown>
        </div>
      </div>
    </div>
  );
}
